import type { Coverage, Manufacturer } from '@features/redux/slices/designSlice';

export const manufacturerFilter = (manufacturer: Manufacturer, searchText: string) => {
    const nameLower = manufacturer.manufacturerName.toLowerCase();
    const searchLower = searchText.toLowerCase();
    return nameLower.includes(searchLower);
};

export const coverageFilter = (coverage: Coverage, searchText: string) => {
    const searchLower = searchText.toLowerCase();
    const nameLower = coverage.coverageName.toLowerCase();
    return nameLower.includes(searchLower);
};

export type Filter = {
    [key: string]: { [key: string]: boolean };
};

export const getFilterParams = (filters: Filter, replacements: Record<string, string> = {}) => {
    return Object.entries(filters).reduce((acc: { [key: string]: string }, [key, value]) => {
        const enabledFilters = Object.keys(value).filter((valueKey) => value[valueKey]);
        const keyToUse = replacements[key] || key;

        return enabledFilters.length ? { ...acc, [keyToUse]: enabledFilters.join(',') } : acc;
    }, {});
};

export const isFilterSelected = <T extends Record<string, { [key: string]: boolean }>>(filters: T, key: string) => {
    const selected = filters[key] ?? {};
    return Object.values(selected).some(Boolean);
};

export function cascadeOptions<T>({
    options,
    parent,
    map,
    idKey,
}: {
    options: T[];
    parent: Record<string, boolean>;
    map: Record<string, number[]>;
    idKey: keyof T;
}): T[] {
    // Grab an array of all IDs that are selected.
    const selected = Object.entries(parent)
        // Only select the ones that are marked as selected.
        .filter((data) => data[1])
        // Convert them all to numbers.
        .map(([id]) => +id);

    // If no specific parent IDs are selected, just use the
    // full options.
    if (!selected.length) return options;

    // Grab all child IDs for each parent ID.
    const targets = selected.flatMap((id) => map[id]);

    // Filter through the options, only keeping the ones where the
    // child ID is within the list.
    return options.filter((item) => targets.includes(+item[idKey]));
}

export const newDisabler = <Managed extends string>(managed: Managed[]) => {
    return (option: Managed, filters: Record<string, any>) => {
        // Don't disabled it if the specified filter is selected
        if (isFilterSelected(filters, option)) return false;
        const position = managed.indexOf(option);

        // If any child filters are selected, disabled this parent one
        return managed.some((name, i) => {
            return position < i && isFilterSelected(filters, name);
        });
    };
};
